import {HTTPError, HTTPCodesEnum} from '@jetbrains/teamcity-ui/dist/services'
import type {QueryArgFrom} from '@reduxjs/toolkit/query'
import * as React from 'react'
import {Fragment} from 'react'
import type {LoaderFunctionArgs, RouteObject} from 'react-router'
import {createBrowserRouter, redirect} from 'react-router'
import type {IEnvironment} from 'relay-runtime'

import {loadBuildsData} from '../../actions/builds'
import getEnvironment from '../../relay/getEnvironment'
import {getAgentPreviewsArg, getSingleAgentArg} from '../../rest/agents'
import {getChangesArg} from '../../rest/arguments'
import {getSingleBuildArg} from '../../rest/builds'
import {getBuildTypeArg} from '../../rest/buildTypes'
import {getBuildLocator, getLocator} from '../../rest/locators'
import {getProjectPageArg, getSingleProjectArg, overviewArg, sidebarArg} from '../../rest/projects'
import {getTabsArg} from '../../rest/tabs'
import Routes, {getHref, isAgentsScreen, isProjectSidebarScreen} from '../../routes'
import type {EntryPointRoute} from '../../routes/entryPointRoute'
import {entryPointRoute} from '../../routes/entryPointRoute'
import type {
  AnyQueryDefinition,
  Endpoint,
  LoadedEndpoints,
  PreloadedEndpointsLoaderData,
} from '../../routes/loadEndpoint'
import {loadEndpoint} from '../../routes/loadEndpoint'
import {getBuildsInited, getExtensionEndpoint} from '../../selectors'
import {restApi} from '../../services/rest'
import store from '../../store'
import {
  AGENTS_TAB_PARAMS_KEY,
  BuildPageTabNamesEnum,
  BuildTypePageTabNamesEnum,
  defaultBuildTypePageTabName,
  ROOT_PROJECT_ID,
  STARRED_LOCATOR,
  stringifyId,
  toAgentId,
  toAgentPoolId,
  toAgentTypeId,
  toBuildId,
  toBuildTypeId,
  toChangeId,
  toProjectId,
} from '../../types'
import {parseBranch} from '../../utils/branchNames'
import {
  isInvestigationsPageEnabled,
  isPipelinesEnabledInExclusiveMode,
} from '../../utils/featureToggles'
import {getAvailabilityErrorMessage} from '../../utils/getAvailabilityErrorMessage'
import {notNull} from '../../utils/guards'
import {getPermalinkLocator} from '../../utils/permalinks'
import {queryToObject} from '../../utils/queryParams'
import {getTabParamsKey} from '../../utils/tabs'
import ErrorPage from '../common/ErrorPage/ErrorPage'
import {submitPager} from '../common/Pager/Pager.actions.base'
import {getPager} from '../common/Pager/Pager.selectors'
import {PagerGroup} from '../common/Pager/Pager.types'
import {getTestArg} from '../packages/Tests/Tests.rest'
import {agentPageOptions} from '../pages/AgentsPages/AgentPage/AgentPage.rest'
import {AgentPoolPageEntryPoint} from '../pages/AgentsPages/AgentPoolPage/AgentPoolPage.entryPoint'
import {AgentsPoolsFavoritePageEntryPoint} from '../pages/AgentsPages/AgentPoolsFavoritePage/AgentsPoolsFavoritePage.entryPoint'
import {AgentsOverviewPageEntryPoint} from '../pages/AgentsPages/AgentsOverviewPage/AgentsOverviewPage.entryPoint'
import {AgentsPagesEntryPoint} from '../pages/AgentsPages/AgentsPages.entryPoint'
import {UnauthorizedAgentsPageEntryPoint} from '../pages/AgentsPages/UnauthorizedAgentsPage/UnauthorizedAgentsPage.entryPoint'
import {BuildPageEntryPoint} from '../pages/BuildPage/BuildPage.entryPoint'
import BuildUnknownBuildTypePage from '../pages/BuildPage/BuildPageUnknownBuildType/BuildUnknownBuildTypePage'
import {SnapshotDependenciesModes} from '../pages/BuildPage/DependenciesTab/DependenciesTab.modes'
import {getSnapshotDependenciesLocator} from '../pages/BuildPage/DependenciesTab/DependenciesTab.utils'
import {mapStateToBuildTypeHistoryProps} from '../pages/BuildTypePage/BuildTypeOverviewTab/BuildTypeHistory/BuildTypeHistory.selectors'
import {
  defaultMode,
  Modes as OverviewBuildTypeModes,
} from '../pages/BuildTypePage/BuildTypeOverviewTab/BuildTypeOverviewTab.modes'
import {BuildTypePageEntryPoint} from '../pages/BuildTypePage/BuildTypePage.entryPoint'
import {CreateProjectPageEntryPoint} from '../pages/CreateProjectPage/CreateProjectPage.entryPoint'
import {FavoriteProjectsPageEntryPoint} from '../pages/FavoriteProjectsPage/FavoriteProjectsPage.entryPoint'
import {NotificationsPageEntryPoint} from '../pages/NotificationsPage/NotificationsPage.entryPoint'
import {pipelinesPagesRoutes} from '../pages/PipelinesPages/PipelinesPages.routes'
import {ProjectPageEntryPoint} from '../pages/ProjectPage/ProjectPage.entryPoint'
import {QueuePageEntryPoint} from '../pages/QueuePage/QueuePage.entryPoint'
import {getQueuePageLocator} from '../pages/QueuePage/QueuePage.utils'
import {TestHistoryPageEntryPoint} from '../pages/TestHistoryPage/TestHistoryPage.entryPoint'

import {AppAgentEntryPoint} from './Agent/AppAgent.entryPoint'
import {AppAgentOverviewEntryPoint} from './AgentOverview/AppAgentOverview.entryPoint'
import {AppAgentTypeEntryPoint} from './AgentType/AppAgentType.entryPoint'
import {AppEntryPoint} from './App.entryPoint'
import {getAvailabilityResponse} from './App.selectors'
import {projectsSidebarApi} from './SidebarPanel/SidebarPanelContent/ProjectsSidebar/ProjectsSidebar.api'

const FavoriteBuildsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "FavoriteBuildsPage" */
      '../pages/FavoriteBuildsPage/FavoriteBuildsPage'
    ),
)
const CompareBuildsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CompareBuildsPage" */
      '../pages/CompareBuildsPage/CompareBuildsPage.container'
    ),
)
const GuidesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "GuidesPage" */
      '../pages/GuidesPage/GuidesPage'
    ),
)
const ChangePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ChangePage" */
      '../pages/ChangePage/ChangePage'
    ),
)
const ChangesPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ChangesPage" */
      '../pages/ChangesPage/ChangesPage'
    ),
)
const InvestigationsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "InvestigationsPage" */
      '../pages/InvestigationsPage/InvestigationsPage'
    ),
)
type Redirect = Readonly<{
  from: string
  to: string
}>

const redirects: ReadonlyArray<Redirect> = [
  {
    from: Routes.DEPRICATED_FAVORITE_BUILDS,
    to: Routes.FAVORITE_BUILDS,
  },
  {
    from: Routes.DEPRICATED_FAVORITE_PROJECTS,
    to: Routes.FAVORITE_PROJECTS,
  },
  {
    from: '*',
    to: isPipelinesEnabledInExclusiveMode() ? Routes.ALL_PIPELINES : Routes.FAVORITE_PROJECTS,
  },
]

const preloadEndpoint = <D extends AnyQueryDefinition>(
  endpoint: Endpoint<D>,
  arg: QueryArgFrom<D>,
) => store.dispatch(loadEndpoint(endpoint, arg, {subscribe: false}))

const favoriteProjectsRoute: EntryPointRoute<any, any> = {
  path: Routes.FAVORITE_PROJECTS,
  entryPoint: FavoriteProjectsPageEntryPoint,
  loader: ({request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
    const url = new URL(request.url)
    const {onlyFavorites} = queryToObject(url.search)
    return {
      preloadedEndpoints: {
        project: preloadEndpoint(
          restApi.endpoints.getProjectNormalized,
          getSingleProjectArg(ROOT_PROJECT_ID),
        ),
        projects:
          onlyFavorites !== 'false'
            ? preloadEndpoint(restApi.endpoints.getSidebarProjectsNormalized, overviewArg)
            : preloadEndpoint(
                restApi.endpoints.getAllProjectsNormalized,
                getProjectPageArg(ROOT_PROJECT_ID),
              ),
      },
    }
  },
}

const routes: ReadonlyArray<EntryPointRoute<any, any>> = [
  favoriteProjectsRoute,
  {
    path: Routes.AGENTS,
    entryPoint: AgentsPagesEntryPoint,
  },
  {
    path: Routes.FAVORITE_AGENT_POOLS,
    entryPoint: AgentsPoolsFavoritePageEntryPoint,
  },
  {
    path: Routes.AGENTS_OVERVIEW,
    entryPoint: AppAgentOverviewEntryPoint,
    loader: (): PreloadedEndpointsLoaderData => ({
      preloadedEndpoints: {
        tabs: preloadEndpoint(restApi.endpoints.getTabs, getTabsArg(AGENTS_TAB_PARAMS_KEY, true)),
      },
    }),
  },
  {
    path: Routes.DISCONNECTED_AGENTS_OVERVIEW,
    entryPoint: AgentsOverviewPageEntryPoint,
    loader: (): PreloadedEndpointsLoaderData => ({
      preloadedEndpoints: {
        tabs: preloadEndpoint(restApi.endpoints.getTabs, getTabsArg(AGENTS_TAB_PARAMS_KEY, true)),
      },
    }),
  },
  {
    path: Routes.AGENTS_UNAUTHORIZED,
    entryPoint: UnauthorizedAgentsPageEntryPoint,
  },
  {
    path: Routes.AGENT,
    entryPoint: AppAgentEntryPoint,
    loader: ({params}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const agentId = toAgentId(params.agentId ?? 0)
      return {
        preloadedEndpoints: {
          agent: preloadEndpoint(
            restApi.endpoints.getAgentNormalized,
            getSingleAgentArg(agentId, agentPageOptions),
          ),
          tabs: preloadEndpoint(
            restApi.endpoints.getTabs,
            getTabsArg(getTabParamsKey({agentId}), false),
          ),
        },
      }
    },
  },
  {
    path: Routes.AGENT_POOL,
    entryPoint: AgentPoolPageEntryPoint,
  },
  {
    path: Routes.CLOUD_IMAGE,
    entryPoint: AppAgentTypeEntryPoint,
  },
  {
    path: Routes.PROJECT,
    entryPoint: ProjectPageEntryPoint,
    loader: ({params}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const projectId = toProjectId(params.projectId ?? '')
      return {
        preloadedEndpoints: {
          tabs: preloadEndpoint(
            restApi.endpoints.getTabs,
            getTabsArg(getTabParamsKey({projectId})),
          ),
          project: preloadEndpoint(
            restApi.endpoints.getProjectNormalized,
            getSingleProjectArg(projectId),
          ),
          projects: preloadEndpoint(
            restApi.endpoints.getAllProjectsNormalized,
            getProjectPageArg(projectId),
          ),
        },
      }
    },
  },
  {
    path: Routes.FAVORITE_BUILDS,
    Component: FavoriteBuildsPage,
    loader: ({request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const url = new URL(request.url)
      const queryParams = queryToObject(url.search)
      const page = Number(queryParams.page ?? 1)

      return {
        preloadedEndpoints: store.dispatch(
          loadBuildsData({
            isPreload: true,
            locator: getLocator({
              withRunningAndQueued: true,
              baseLocator: STARRED_LOCATOR,
            }),
            withPager: true,
            page,
            requestOptions: {
              withBuildTypeDetails: true,
              withSnapshotDependencies: false,
              withQueuedInfo: true,
              withRunningInfo: true,
              withBuildDetails: true,
              essential: true,
            },
          }),
        ),
      }
    },
  },
  {
    path: Routes.BUILD_TYPE,
    entryPoint: BuildTypePageEntryPoint,
    loader: ({params, request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const url = new URL(request.url)
      const {
        buildTypeTab = defaultBuildTypePageTabName,
        mode = defaultMode,
        branch,
        state: buildState,
        tag,
        'agent-name-pattern': agentPattern,
        page = '1',
      } = queryToObject(url.search)
      const state = store.getState()

      const pageSize = 50

      if (getPager(state, PagerGroup.BUILD).pageSize !== pageSize) {
        store.dispatch(
          submitPager({
            pageSize,
          }),
        )
      }

      const buildTypeId = toBuildTypeId(params.buildTypeId ?? '')

      const preloadedEndpoints: LoadedEndpoints = {}
      preloadedEndpoints.buildType = preloadEndpoint(
        restApi.endpoints.getBuildTypeNormalized,
        getBuildTypeArg(buildTypeId),
      )

      const tabParamsKey = getTabParamsKey({buildTypeId, branch: parseBranch(branch)})
      preloadedEndpoints.tabs = preloadEndpoint(restApi.endpoints.getTabs, getTabsArg(tabParamsKey))

      if (
        buildTypeTab === BuildTypePageTabNamesEnum.OVERVIEW &&
        mode === OverviewBuildTypeModes.BUILDS
      ) {
        const {locator} = mapStateToBuildTypeHistoryProps(store.getState(), {
          buildTypeId,
          branch: parseBranch(branch),
          withCollapsedQueued: true,
          withRunningAndQueued: true,
          buildState: buildState ?? undefined,
          tag,
          agentPattern,
        })
        if (locator != null) {
          Object.assign(
            preloadedEndpoints,
            store.dispatch(
              loadBuildsData({
                isPreload: true,
                locator,
                withPager: true,
                page: Number(page),
                requestOptions: {
                  withBuildTypeDetails: true,
                  withSnapshotDependencies: false,
                  withQueuedInfo: true,
                  withBuildDetails: true,
                  essential: !getBuildsInited(state, locator),
                },
                extraRequestOptions: {
                  withShortBuildDetails: true,
                  withRunningInfo: true,
                },
              }),
            ),
          )
        }
      }

      return {preloadedEndpoints}
    },
  },
  {
    path: Routes.BUILD,
    entryPoint: BuildPageEntryPoint,
    loader: ({params, request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const url = new URL(request.url)
      const {
        buildTab = BuildPageTabNamesEnum.OVERVIEW,
        mode = SnapshotDependenciesModes.TIMELINE,
        branch,
        state,
      } = queryToObject(url.search)

      const buildTypeId = toBuildTypeId(params.buildTypeId ?? '')

      const permalinkLocator = getPermalinkLocator(
        params.buildId ?? '',
        buildTypeId,
        parseBranch(branch),
      )

      const preloadedEndpoints: LoadedEndpoints = {}
      if (permalinkLocator != null) {
        preloadedEndpoints.build = preloadEndpoint(restApi.endpoints.getBuild, {
          buildLocator: permalinkLocator,
          fields: 'id',
          essential: true,
        })
      } else {
        const buildId = toBuildId(params.buildId ?? 0)

        if (buildTab === BuildPageTabNamesEnum.DEPENDENCIES) {
          const dependenciesLocator = getLocator({
            baseLocator: getSnapshotDependenciesLocator(buildId, mode),
            withRunningAndQueued: true,
            buildState: state ?? undefined,
          })

          if (dependenciesLocator != null) {
            Object.assign(
              preloadedEndpoints,
              store.dispatch(
                loadBuildsData({
                  isPreload: true,
                  locator: dependenciesLocator,
                  requestOptions: {
                    withBuildTypeDetails: true,
                    withSnapshotDependencies: true,
                    withQueuedInfo: true,
                    withRunningInfo: true,
                    withBuildDetails: mode === SnapshotDependenciesModes.LIST,
                    essential: true,
                  },
                }),
              ),
            )
          }
        }

        const tabParamsKey = getTabParamsKey({buildId})
        preloadedEndpoints.tabs = preloadEndpoint(
          restApi.endpoints.getTabs,
          getTabsArg(tabParamsKey, false),
        )
        preloadedEndpoints.buildNormalizedAsList = preloadEndpoint(
          restApi.endpoints.getBuildNormalizedAsList,
          getSingleBuildArg(getBuildLocator(buildId!), {
            withBuildTypeDetails: true,
          }),
        )
      }

      preloadedEndpoints.buildType = preloadEndpoint(
        restApi.endpoints.getBuildTypeNormalized,
        getBuildTypeArg(buildTypeId),
      )

      return {preloadedEndpoints}
    },
  },
  {
    path: Routes.BUILD_UNKNOWN_BUILDTYPE,
    Component: BuildUnknownBuildTypePage,
  },
  {
    path: Routes.COMPARE_BUILDS,
    Component: CompareBuildsPage,
    loader: ({params, request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const sourceId = toBuildId(params.buildIdSource!)
      const url = new URL(request.url)
      const queryParams = queryToObject(url.search)
      const preloadedEndpoints: LoadedEndpoints = {
        sourceBuild: preloadEndpoint(
          restApi.endpoints.getBuildNormalizedAsList,
          getSingleBuildArg(getBuildLocator(sourceId)),
        ),
      }
      if (queryParams.withBuildId != null) {
        const compareWithId = toBuildId(queryParams.withBuildId)
        preloadedEndpoints.compareWithBuild = preloadEndpoint(
          restApi.endpoints.getBuildNormalizedAsList,
          getSingleBuildArg(getBuildLocator(compareWithId)),
        )
        const {endpoint} = getExtensionEndpoint(store.getState(), 'builds-diff') ?? {}
        if (endpoint != null) {
          preloadedEndpoints.compareBuilds = preloadEndpoint(
            restApi.endpoints.getCompareBuildsList,
            {
              endpoint,
              sourceId,
              compareWithId,
            },
          )
        }
      }
      return {preloadedEndpoints}
    },
  },
  {
    path: Routes.TEST,
    entryPoint: TestHistoryPageEntryPoint,
    loader: ({params, request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const {testId} = params
      const url = new URL(request.url)
      const {currentProjectId} = queryToObject(url.search)
      const testLocator = `id:${testId}`
      const muteAndInvestigationLocator = `affectedProjectAndParents:(id:${currentProjectId})`
      return {
        preloadedEndpoints: {
          test: preloadEndpoint(
            restApi.endpoints.getTest,
            getTestArg(testLocator, muteAndInvestigationLocator),
          ),
        },
      }
    },
  },
  {
    path: Routes.CHANGE,
    Component: ChangePage,
    loader: ({params, request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const url = new URL(request.url)
      const queryParams = queryToObject(url.search)
      const changeId = toChangeId(params.changeId ?? '')
      const personal = queryParams?.personal === 'true'
      const locator = `id:${stringifyId(changeId)},personal:${personal}`

      return {
        preloadedEndpoints: {
          changes: preloadEndpoint(restApi.endpoints.getAllChanges, getChangesArg(locator)),
          tabs: preloadEndpoint(
            restApi.endpoints.getTabs,
            getTabsArg(getTabParamsKey({changeId, personal}), true),
          ),
          projects:
            queryParams.showHierarchy === 'true' &&
            preloadEndpoint(restApi.endpoints.getSidebarProjectsNormalized, sidebarArg),
        },
      }
    },
  },
  {
    path: Routes.CHANGES,
    Component: ChangesPage,
    loader: ({request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const url = new URL(request.url)
      const {showHierarchy} = queryToObject(url.search)
      return {
        preloadedEndpoints: {
          projects:
            showHierarchy === 'true' &&
            preloadEndpoint(restApi.endpoints.getSidebarProjectsNormalized, sidebarArg),
        },
      }
    },
  },
  {
    path: Routes.GUIDES,
    Component: GuidesPage,
  },
  {
    path: Routes.QUEUE,
    entryPoint: QueuePageEntryPoint,
    loader: ({request}: LoaderFunctionArgs): PreloadedEndpointsLoaderData => {
      const url = new URL(request.url)
      const queryParams = queryToObject(url.search)

      const page = (queryParams.page && parseInt(queryParams.page, 10)) || 1
      store.dispatch(
        submitPager({
          precountedPages: 3,
          pageSize: 50,
          lookupLimit: 10000,
          lookupDelta: 10000,
        }),
      )
      const activeAgentPoolIdString = queryParams.agentPoolId
      const activeAgentPoolId =
        activeAgentPoolIdString != null ? toAgentPoolId(activeAgentPoolIdString) : null
      const onlyMyPersonal = queryParams.onlyMyPersonal === 'true'
      const locator = getQueuePageLocator(activeAgentPoolId, onlyMyPersonal)

      return {
        preloadedEndpoints: store.dispatch(
          loadBuildsData({
            isPreload: true,
            locator,
            withPager: true,
            page,
            requestOptions: {
              customEndpoint: '/buildQueue',
              withBuildTypeDetails: true,
              withSnapshotDependencies: false,
              withQueuedInfo: true,
              withBuildDetails: true,
              essential: true,
            },
            extraRequestOptions: {
              customEndpoint: '/buildQueue',
              withShortBuildDetails: true,
              withPipelineRuns: true,
              withAnchorBuild: true,
              essential: true,
            },
          }),
        ),
      }
    },
  },
  isInvestigationsPageEnabled
    ? {
        path: Routes.INVESTIGATIONS,
        Component: InvestigationsPage,
      }
    : null,
  ...pipelinesPagesRoutes,
  {path: Routes.CREATE_PROJECT, entryPoint: CreateProjectPageEntryPoint},
  {path: Routes.NOTIFICATIONS, entryPoint: NotificationsPageEntryPoint},

  // !!! DON'T FORGET !!!
  // !!! 1) ADD the mapping to web-deployment/webapp/WEB-INF/web.xml AND /react-ui/src/routes/shared-routes.json !!!
  // !!! 2) Restart the server
].filter(notNull)

export const getRoutes = (environment: IEnvironment) =>
  routes.map((route): RouteObject => {
    const {loader, ...rest} = entryPointRoute(route, environment)
    return {
      ErrorBoundary: ErrorPage,
      ...rest,
      loader(arg) {
        const {params, request} = arg
        const url = new URL(request.url)

        const availabilityResponse = getAvailabilityResponse(store.getState(), url.pathname)
        if (availabilityResponse != null && availabilityResponse.statusCode !== HTTPCodesEnum.OK) {
          const {buildId, buildTypeId, projectId, agentId, agentTypeId, agentPoolId} = params
          throw new HTTPError(
            getAvailabilityErrorMessage({
              error: availabilityResponse,
              buildId: buildId != null ? toBuildId(buildId) : null,
              buildTypeId: buildTypeId != null ? toBuildTypeId(buildTypeId) : null,
              projectId: projectId != null ? toProjectId(projectId) : null,
              agentId: agentId != null ? toAgentId(agentId) : null,
              agentTypeId: agentTypeId != null ? toAgentTypeId(agentTypeId) : null,
              agentPoolId: agentPoolId != null ? toAgentPoolId(agentPoolId) : null,
            }) ?? '',
            availabilityResponse.statusCode,
          )
        }

        return typeof loader === 'function' ? (loader(arg) ?? {}) : {}
      },
    }
  })
export const createAppRouter = () =>
  createBrowserRouter([
    entryPointRoute(
      {
        path: Routes.BASE,
        entryPoint: AppEntryPoint,
        loader: ({request}): PreloadedEndpointsLoaderData => {
          const url = new URL(request.url)
          const preloadedEndpoints: LoadedEndpoints = {}
          if (isProjectSidebarScreen(url.pathname)) {
            preloadedEndpoints.buildTypesCount = preloadEndpoint(
              projectsSidebarApi.endpoints.getBuildTypesCount,
              undefined,
            )
          } else if (isAgentsScreen(url.pathname)) {
            preloadedEndpoints.agents = preloadEndpoint(
              restApi.endpoints.getAllAgentPreviewsNormalized,
              getAgentPreviewsArg(),
            )
            preloadedEndpoints.poolPermissions = preloadEndpoint(
              restApi.endpoints.getPoolPermissions,
              undefined,
            )
          }
          if (getAvailabilityResponse(store.getState(), url.pathname) == null) {
            preloadedEndpoints.routeAvailability = preloadEndpoint(
              restApi.endpoints.getRouteAvailability,
              url.pathname,
            )
          }
          return {preloadedEndpoints}
        },
        ErrorBoundary: ErrorPage,
        HydrateFallback: Fragment,
        children: [
          ...redirects.map(({from, to}) => ({
            path: from,
            loader() {
              throw redirect(getHref(to))
            },
            element: null,
          })),
          ...getRoutes(getEnvironment()),
        ],
      },
      getEnvironment(),
    ),
    {
      path: '*',
      loader() {
        throw new HTTPError('Page not found', HTTPCodesEnum.NOT_FOUND)
      },
      ErrorBoundary: ErrorPage,
    },
  ])
export type Router = ReturnType<typeof createBrowserRouter>
